import dayjs, { type Dayjs } from "dayjs"

import { type Event } from "../../../types/types.ts"

// A display convenience only — EZBuild has no working-hours configuration,
// so this is never a claim that anyone is unavailable outside it. It always
// expands to cover real events (see computeAvailabilityWindow) and never
// shrinks below it.
const DEFAULT_START_HOUR = 8
const DEFAULT_END_HOUR = 18

interface AvailabilityWindow {
  startHour: number
  endHour: number
}

interface ClampedEventTime {
  start: Dayjs
  end: Dayjs
}

// A timed event's start/end, clamped to the displayed day — a multi-day
// event that only partly overlaps this day must not distort the axis with
// an hour that actually belongs to a different calendar day.
function clampEventToDay(
  event: Event,
  day: Dayjs,
  timezone: string
): ClampedEventTime | null {
  if (event.all_day) return null

  const start = dayjs(event.start_at).tz(timezone)
  if (!start.isValid()) return null
  const end = event.end_at
    ? dayjs(event.end_at).tz(timezone)
    : start.add(30, "minute")

  const dayStart = day.startOf("day")
  const dayEnd = day.endOf("day")
  const clampedStart = start.isBefore(dayStart) ? dayStart : start
  const clampedEnd = end.isAfter(dayEnd) ? dayEnd : end

  if (!clampedEnd.isAfter(clampedStart)) return null

  return { start: clampedStart, end: clampedEnd }
}

// The 8 AM–6 PM default, expanded outward — never inward — to cover every
// timed event actually happening that day, across whichever people are
// shown. All-day events don't affect it; they're shown separately.
function computeAvailabilityWindow(
  eventsByMemberId: Record<string, Event[]>,
  day: Dayjs,
  timezone: string
): AvailabilityWindow {
  let startHour = DEFAULT_START_HOUR
  let endHour = DEFAULT_END_HOUR

  Object.values(eventsByMemberId).forEach((events) => {
    events.forEach((event) => {
      const clamped = clampEventToDay(event, day, timezone)
      if (!clamped) return

      startHour = Math.min(startHour, clamped.start.hour())
      const endHourValue =
        clamped.end.minute() > 0 ? clamped.end.hour() + 1 : clamped.end.hour()
      endHour = Math.max(endHour, endHourValue)
    })
  })

  return { startHour, endHour }
}

// A timed event's position within the window, as a left/width percentage
// pair ready for absolute positioning. Returns null for an all-day event or
// one that ends up with no overlap in the window (shouldn't happen once the
// window has already been expanded to include it, but stays defensive).
function eventBlockPosition(
  event: Event,
  day: Dayjs,
  timeWindow: AvailabilityWindow,
  timezone: string
): { leftPct: number; widthPct: number } | null {
  const clamped = clampEventToDay(event, day, timezone)
  if (!clamped) return null

  const dayStart = day.startOf("day")
  const windowStart = dayStart.add(timeWindow.startHour, "hour")
  const windowEnd = dayStart.add(timeWindow.endHour, "hour")
  const windowMinutes = windowEnd.diff(windowStart, "minute")
  if (windowMinutes <= 0) return null

  const start = clamped.start.isBefore(windowStart)
    ? windowStart
    : clamped.start
  const end = clamped.end.isAfter(windowEnd) ? windowEnd : clamped.end
  if (!end.isAfter(start)) return null

  return {
    leftPct: (start.diff(windowStart, "minute") / windowMinutes) * 100,
    widthPct: (end.diff(start, "minute") / windowMinutes) * 100
  }
}

// A block never renders narrower than this — otherwise a 15-minute event in
// a multi-hour window is a handful of literal pixels, effectively invisible
// and unclickable. It stays exactly positioned by its real start time
// either way; only its rendered width gets this floor.
const MIN_BLOCK_PX = 28

// Below this, a second line can only ever show a handful of characters —
// exactly the "Walsh Ho..." fragment this exists to prevent. Above it, the
// full name renders (its own `ellipsis` is still there as a backstop for a
// genuinely long name, not as the normal case).
const MIN_WIDTH_FOR_RECORD_NAME_PX = 90

// The block's real rendered width in pixels — a plain percentage-of-window
// figure on its own says nothing about whether it's 40px or 400px on
// screen; that depends on how wide the lane itself actually rendered, which
// varies with the viewport and isn't something a duration threshold alone
// (as the grid views use) can account for.
function eventBlockPixelWidth(widthPct: number, laneWidthPx: number): number {
  return (widthPct / 100) * laneWidthPx
}

// Whether a block measured this wide has room for a second (secondary
// record — Lead/Project/Customer) line without truncating mid-word. Pure so
// the threshold itself is directly testable — jsdom has no real layout
// engine, so the actual rendered width can only be verified in a browser,
// but the decision this makes *given* a width is ordinary logic.
function hasRoomForRecordName(blockPx: number): boolean {
  return blockPx >= MIN_WIDTH_FOR_RECORD_NAME_PX
}

export {
  clampEventToDay,
  computeAvailabilityWindow,
  DEFAULT_END_HOUR,
  DEFAULT_START_HOUR,
  eventBlockPixelWidth,
  eventBlockPosition,
  hasRoomForRecordName,
  MIN_BLOCK_PX,
  MIN_WIDTH_FOR_RECORD_NAME_PX,
  type AvailabilityWindow
}
